import React from "react";
import { useState, useEffect } from "react";

const GradientHeader = ({ logoSrc, justifyContent, showGradient, headerColor }) => {
  const [imgError, setImgError] = useState(false);
  const [logoStyle, setLogoStyle] = useState({
    height: "auto",
    maxHeight: "60px",
    maxWidth: "120px",
    width: "auto",
    objectFit: "contain",
    marginRight: "0px",
  });

  const checkLogoVisibility = (src, error) => {
    return src && src !== "" && src !== "null" && !error;
  };

  const handleImageLoad = (e) => {
    const img = e.target;
    const aspectRatio = img.naturalWidth / img.naturalHeight;
    const viewportWidth = window.innerWidth;

    // Base sizes that will be adjusted by screen width
    let baseMaxWidth = 120;
    let baseMaxHeight = 60;
    let margin = "0px"; // Default margin

    // Adjust sizes according to viewport width
    if (viewportWidth >= 1920) {
      // Extra large screens (4K, large monitors)
      baseMaxWidth = 140;
      baseMaxHeight = 70;
    } else if (viewportWidth >= 1440) {
      // Large screens (laptops, desktops)
      baseMaxWidth = 130;
      baseMaxHeight = 65;
    } else if (viewportWidth >= 1024) {
      // Medium screens (tablets landscape)
      baseMaxWidth = 120;
      baseMaxHeight = 60;
    } else if (viewportWidth >= 768) {
      // Small tablets
      baseMaxWidth = 100;
      baseMaxHeight = 50;
    } else if (viewportWidth >= 576) {
      // Large phones
      baseMaxWidth = 100;
      baseMaxHeight = 50;
    } else {
      // Small phones
      baseMaxWidth = 80;
      baseMaxHeight = 40;
    }

    // Then adjust for aspect ratio with more conservative scaling
    if (aspectRatio > 2.5) {
      // Very wide logos (like banners)
      setLogoStyle((prev) => ({
        ...prev,
        maxWidth: `${Math.min(baseMaxWidth * 1.5, 200)}px`, // Cap at 180px max
        maxHeight: `${Math.min(baseMaxHeight * 0.9, 60)}px`, // Cap height
        margin: margin,
      }));
    } else if (aspectRatio > 1.5) {
      // Wide logos
      setLogoStyle((prev) => ({
        ...prev,
        maxWidth: `${Math.min(baseMaxWidth * 1.1, 160)}px`, // Cap at 160px max
        maxHeight: `${Math.min(baseMaxHeight * 0.9, 60)}px`, // Cap height
        margin: margin,
      }));
    } else if (aspectRatio < 0.7) {
      // Very tall logos
      setLogoStyle((prev) => ({
        ...prev,
        maxWidth: `${Math.min(baseMaxWidth * 0.6, 80)}px`, // Cap width
        maxHeight: `${Math.min(baseMaxHeight * 1.2, 80)}px`, // Cap at 80px max
        margin: margin,
      }));
    } else if (aspectRatio < 1) {
      // Tall logos
      setLogoStyle((prev) => ({
        ...prev,
        maxWidth: `${Math.min(baseMaxWidth * 0.8, 100)}px`, // Cap width
        maxHeight: `${Math.min(baseMaxHeight * 1.1, 70)}px`, // Cap height
        margin: margin,
      }));
    } else {
      // Regular aspect ratio (close to square)
      setLogoStyle((prev) => ({
        ...prev,
        maxWidth: `${baseMaxWidth}px`,
        maxHeight: `${baseMaxHeight}px`,
        margin: margin,
      }));
    }
  };

  // Add resize listener
  useEffect(() => {
    const handleResize = () => {
      // Trigger handleImageLoad again on resize
      const img = document.querySelector(".theme-logo-image");
      if (img) {
        handleImageLoad({ target: img });
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  if (!checkLogoVisibility(logoSrc, imgError)) return null;

  const defaultColor = "#1E1665";
  const resolvedHeaderColor = headerColor || defaultColor;

  return (
    <div
      style={{
        width: "100%",
        paddingTop: "20px",
        paddingBottom: "20px",
        background: showGradient
        ? `linear-gradient(
            to bottom,
            ${resolvedHeaderColor} 0%,
            rgba(58,94,255,0) 100%
          )`
        : "transparent",
        display: "flex",
        justifyContent: justifyContent || "flex-start",
        alignItems: "center",
        minHeight: "60px",
        paddingRight: justifyContent === "flex-end" ? "28px" : "0px",
        paddingLeft: justifyContent === "flex-start" ? "28px" : "0px",
      }}
    >
      <img
        className="img-fluid theme-logo-image"
        src={logoSrc}
        alt="Logo"
        style={logoStyle}
        onError={() => setImgError(true)}
        onLoad={handleImageLoad}
      />
    </div>
  );
};

export default GradientHeader;
